import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { Checkmark } from '@sas/react-svg';
import cx from 'classnames';
import { setAlpha } from '../../design-tokens';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { useKeyboardTooltip } from '../../hooks/useKeyboardTooltip';
import { renderDesignTokens, useDesignTokens } from '../../theme';
import { setRef } from '../../util/ref-util';
import { postfixTestId } from '../../util/testId';
import { BaseHelpPopup } from '../HelpPopup/__internal__/';
import { Icon } from '../Icon';
import { Label } from '../Label';
import { Text } from '../Text';
import cn from './Checkbox.css.json';
import './Checkbox.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useHtmlId = createUseHtmlId('Checkbox');
export const UnthemedCheckbox = /*#__PURE__*/forwardRef(function UnthemedCheckbox(props, ref) {
  const descriptionId = useHtmlId();
  const dt = useDesignTokens();
  const labelRef = useRef(null);
  const [marginTopCalc, setMarginTopCalc] = useState(undefined);
  useEffect(() => {
    // line-height patch specific to checkbox to help keep checkbox and its label centered
    // NOTE: this will break if the unit is not px
    const globalFontSizeStyle = parseFloat(window.getComputedStyle(document.documentElement).fontSize);
    const globalFontSize = isNaN(globalFontSizeStyle) ? 16 : globalFontSizeStyle;
    let labelFontSizeInPixels = parseFloat(dt.enableNovaTokens ? dt.tokens['font-size-body-text-m'] : dt.reportTokens.font.size.m) * globalFontSize;
    if (labelRef.current) {
      labelFontSizeInPixels = parseFloat(window.getComputedStyle(labelRef.current).getPropertyValue('font-size'));
    }
    const labelHeight = Math.round(labelFontSizeInPixels * (dt.enableNovaTokens ? parseFloat(dt.tokens['font-space-line-height']) : dt.reportTokens.font.lineHeight));
    const boxHeight = 0.875 * globalFontSize; // the box size is 0.875rem
    // the height of the checkbox (0.875rem) is equal to the label font size (font-size-m).
    // Only define the custom margin if a label is supplied.
    setMarginTopCalc(props.label ? `${Math.round((labelHeight - boxHeight) / 2)}px` : undefined);
  }, [dt.enableNovaTokens, dt.reportTokens.font.lineHeight, dt.reportTokens.font.size.m, dt.tokens, props.label]);
  const setKeyboardTooltipRef = useKeyboardTooltip({
    traverseChildren: true
  });
  return /*#__PURE__*/_jsxs("div", {
    className: cx(cn.container, !props.label && cn.noLabel, props.className),
    style: props.style,
    "data-testid": props.testId,
    children: [/*#__PURE__*/_jsxs("div", {
      ref: el => {
        setKeyboardTooltipRef(el);
        setRef(ref, el);
      },
      role: "checkbox",
      "data-testid": postfixTestId(props.testId, 'checkbox'),
      "aria-checked": props.checked,
      className: cx(cn.main, props.disabled && cn.disabled, props.readOnly && cn.readOnly),
      "aria-disabled": props.disabled,
      "aria-readonly": props.readOnly,
      "aria-required": props.required,
      "aria-describedby": props.description ? descriptionId : undefined,
      "aria-labelledby": props.ariaLabelledBy,
      "aria-label": props.ariaLabel,
      title: props.tooltip,
      onClick: e => {
        if (!props.disabled && !props.readOnly && !e.isDefaultPrevented()) {
          e.preventDefault();
          props.onPress?.(e);
        }
      },
      tabIndex: props.focusable === false ? -1 : 0 // Setting to -1 so that when focusable changes from true to false, focus is not lost
      ,
      onKeyDown: e => {
        if (!props.disabled && !props.readOnly && !e.isDefaultPrevented() && e.key === ' ') {
          e.preventDefault();
          props.onPress?.(e);
        }
      },
      children: [
      // Render the label first so a screen reader will announce the label before the tooltip.
      props.label && /*#__PURE__*/_jsx(Label, {
        testId: postfixTestId(props.testId, 'label'),
        ...props.labelProps,
        className: cx(cn.label, props.labelProps?.className),
        theme: () => {
          return {
            '--label-text-cursor': 'inherit',
            '--label-fg': 'var(--checkbox-label-fg)'
          };
        },
        disabled: props.disabled,
        wrapText: props.wrapText,
        required: props.required && !props.readOnly,
        suppressTooltip: !!props.tooltip,
        ref: el => {
          setRef(labelRef, el);
          setRef(props.labelProps?.ref, el);
        },
        children: props.label
      }), /*#__PURE__*/_jsx("span", {
        className: cx(cn.box, {
          [cn.mixed]: props.checked === 'mixed',
          [cn.selected]: props.checked === true
        }),
        style: {
          marginTop: marginTopCalc
        },
        children: props.checked === true ? /*#__PURE__*/_jsx(Icon, {
          icon: Checkmark,
          tokens: () => ({
            fg: 'var(--checkbox-icon-fg)'
          }),
          tooltip: "",
          ariaLabel: ""
        }) : null
      })]
    }), props.helpPopup ? /*#__PURE__*/_jsx(BaseHelpPopup, {
      className: cn.helpButton,
      ...props.helpPopup,
      additionalAriaText: props.label ?? props.ariaLabel
    }) : null, props.description && /*#__PURE__*/_jsx(Text, {
      id: descriptionId,
      testId: postfixTestId(props.testId, 'description'),
      className: cn.description,
      disabled: props.disabled,
      wrapText: props.wrapDescription,
      tokens: () => ({
        fg: 'var(--checkbox-description-fg)'
      }),
      children: props.description
    })]
  });
});
export const Checkbox = /*#__PURE__*/forwardRef(function Checkbox(allProps, ref) {
  const {
    style: userStyle,
    ...props
  } = allProps;
  const dt = useDesignTokens();
  const themeStyle = useMemo(() => ({
    ...getCheckboxDesignTokens(dt, {
      disabled: props.disabled,
      readOnly: props.readOnly,
      description: props.description
    }),
    ...props.theme?.(dt)
  }), [dt, props]);
  return /*#__PURE__*/_jsx(UnthemedCheckbox, {
    ...props,
    style: {
      ...themeStyle,
      ...userStyle
    },
    ref: ref
  });
});
export const getCheckboxDesignTokens = (dt, opts) => {
  const {
    disabled,
    readOnly,
    description
  } = opts ?? {};
  const checkboxTokens = {
    disabled: {
      bd: dt.tokens['color-action-border-on-off-disabled'],
      bg: dt.tokens['color-action-background-on-off-disabled'],
      fg: dt.tokens['color-action-text-on-off-disabled']
    },
    disabledSelected: {
      bd: dt.tokens['color-action-border-on-off-selected-disabled'],
      bg: dt.tokens['color-action-background-on-off-selected-disabled'],
      fg: dt.tokens['color-action-text-on-off-selected-disabled']
    },
    normal: {
      bd: dt.tokens['color-action-border-on-off-normal'],
      bg: dt.tokens['color-action-background-on-off-normal'],
      fg: dt.tokens['color-action-text-on-off-normal']
    },
    readOnly: {
      bd: dt.tokens['color-action-border-on-off-read-only'],
      bg: dt.tokens['color-action-background-on-off-read-only'],
      fg: dt.tokens['color-action-text-on-off-read-only']
    },
    selected: {
      bd: 'transparent',
      bg: dt.tokens['color-action-background-on-off-selected'],
      fg: dt.tokens['color-action-text-on-off-selected']
    }
  };

  /*
   * List sets these tokens at the root of the List for performance considerations.
   * Without knowledge of the state of each checkbox in the list, it needs a separate token
   * for each state.
   */
  return {
    ...renderDesignTokens({
      'spacing-s': dt.tokens['space-base-s'],
      'spacing-xs': dt.tokens['space-base-xs'],
      'spacing-xxs': dt.tokens['space-base-xxs'],
      'button-bd': dt.tokens['size-border-width-s'],
      'font-size-xs': dt.tokens['font-size-body-text-xs'],
      'font-size-m': dt.tokens['font-size-body-text-m'],
      'row-gap': description ? dt.tokens['space-base-xs'] : 0,
      'label-fg': disabled ? checkboxTokens.disabled.fg : dt.tokens['color-body-text'],
      'description-fg': disabled ? dt.tokens['color-action-text-control-disabled'] : dt.tokens['color-label-text'],
      'icon-fg': disabled && readOnly ? checkboxTokens.disabled.fg : disabled ? checkboxTokens.disabledSelected.fg : readOnly ? dt.tokens['color-action-icon-on-off-read-only'] : checkboxTokens.selected.fg,
      ...checkboxTokens.normal
    }, 'checkbox'),
    ...renderDesignTokens(checkboxTokens.selected, 'checkbox-selected'),
    ...renderDesignTokens(checkboxTokens.disabled, 'checkbox-disabled'),
    ...renderDesignTokens(checkboxTokens.disabledSelected, 'checkbox-disabled-selected'),
    ...renderDesignTokens(checkboxTokens.readOnly, 'checkbox-read-only'),
    ...(!dt.enableNovaTokens && getCheckboxLegacyDesignTokens(dt, opts))
  };
};
export const getCheckboxLegacyDesignTokens = (dt, opts) => {
  const tokens = dt.stateSecondaryToggle;
  let readOnlyTokens = {
    fg: dt.c.n_3,
    bg: dt.c.white,
    bd: dt.c.n2
  };
  let descriptionTextColor = dt.c.n_1;
  switch (dt.themeVariant) {
    case 'dark':
      readOnlyTokens = {
        fg: dt.c.white,
        bg: dt.c.p_3,
        bd: setAlpha(dt.c.white, 0.2)
      };
      descriptionTextColor = setAlpha(dt.c.white, 0.8);
      break;
    case 'high-contrast':
      readOnlyTokens = {
        fg: dt.c.white,
        bg: dt.c.black,
        bd: dt.c.white
      };
      descriptionTextColor = dt.c.white;
      break;
  }
  return {
    ...renderDesignTokens({
      'font-size-s': dt.reportTokens.font.size.s,
      'font-size-m': dt.reportTokens.font.size.m,
      'label-fg': opts?.disabled ? tokens.disabled.fg : dt.c.n_3,
      'description-fg': opts?.disabled ? tokens.disabled.fg : descriptionTextColor,
      ...tokens.normal,
      'icon-fg': opts?.disabled && opts?.readOnly ? tokens.disabled.fg : opts?.disabled ? tokens.disabledSelected.fg : opts?.readOnly ? readOnlyTokens.fg : tokens.selected.fg
    }, 'checkbox'),
    ...renderDesignTokens(tokens.selected, 'checkbox-selected'),
    ...renderDesignTokens(tokens.disabled, 'checkbox-disabled'),
    ...renderDesignTokens(tokens.disabledSelected, 'checkbox-disabled-selected'),
    ...renderDesignTokens(readOnlyTokens, 'checkbox-read-only')
  };
};

/**
 * @deprecated use `getCheckboxDesignTokens()` instead
 */
export const getCheckboxThemeTokens = (dt, state) => getCheckboxDesignTokens(dt, state);
//# sourceMappingURL=Checkbox.js.map